import { Modal, Typography } from "@material-ui/core";
import { Box, CircularProgress, Switch, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import { DOMAIN_API_HOST } from "../../../configs/main";
import "./VisibilityBlocksModal.scss";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 620,
  maxHeight: "90vh",
  overflowY: "auto",
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
};

const EMPTY_SETTINGS = {
  hide_campaign_settings: false,
  hide_postview_download: false,
  hide_ads_filter: false,
  hide_goals_filter: false,
  hide_stat_ctr: false,
  hide_stat_ecpm: false,
  hide_stat_cr: false,
  hide_stat_cpa: false,
  stats_visible_from: null,
  charge_campaign_ids: [],
  updated_by_email: null,
  updated_at: null,
};

function VisibilityBlocksModal({ open, client, handleClose }) {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(EMPTY_SETTINGS);
  const [campaigns, setCampaigns] = useState([]);

  useEffect(() => {
    if (!open || !client?.id) return;

    const load = async () => {
      setLoading(true);
      try {
        const headers = new Headers();
        headers.append("Content-Type", "application/json");
        headers.append(
          "Authorization",
          "Bearer " + localStorage.getItem("admin_token")
        );
        const res = await fetch(
          `${DOMAIN_API_HOST}/api/v1/admin/clients/${client.id}/cabinet-visibility`,
          { method: "GET", headers }
        );
        if (!res.ok) {
          throw new Error("Не удалось загрузить настройки");
        }
        const data = await res.json();
        setSettings({
          ...EMPTY_SETTINGS,
          ...(data.settings || {}),
          charge_campaign_ids: (data.settings?.charge_campaign_ids || []).map(String),
        });
        setCampaigns(data.chargeable_campaigns || []);
      } catch (e) {
        enqueueSnackbar(e.message || "Ошибка загрузки", {
          variant: "error",
          autoHideDuration: 2500,
          anchorOrigin: { vertical: "top", horizontal: "right" },
        });
        handleClose();
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [open, client?.id]);

  const setFlag = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const toggleCampaign = (campaignId, enabled) => {
    const id = String(campaignId);
    setSettings((prev) => {
      const set = new Set((prev.charge_campaign_ids || []).map(String));
      if (enabled) set.add(id);
      else set.delete(id);
      return { ...prev, charge_campaign_ids: Array.from(set) };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const headers = new Headers();
      headers.append("Content-Type", "application/json");
      headers.append(
        "Authorization",
        "Bearer " + localStorage.getItem("admin_token")
      );
      const body = {
        hide_campaign_settings: Boolean(settings.hide_campaign_settings),
        hide_postview_download: Boolean(settings.hide_postview_download),
        hide_ads_filter: Boolean(settings.hide_ads_filter),
        hide_goals_filter: Boolean(settings.hide_goals_filter),
        hide_stat_ctr: Boolean(settings.hide_stat_ctr),
        hide_stat_ecpm: Boolean(settings.hide_stat_ecpm),
        hide_stat_cr: Boolean(settings.hide_stat_cr),
        hide_stat_cpa: Boolean(settings.hide_stat_cpa),
        stats_visible_from: settings.stats_visible_from || null,
        charge_campaign_ids: settings.charge_campaign_ids || [],
      };
      const res = await fetch(
        `${DOMAIN_API_HOST}/api/v1/admin/clients/${client.id}/cabinet-visibility`,
        {
          method: "PUT",
          headers,
          body: JSON.stringify(body),
        }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Не удалось сохранить");
      }
      const data = await res.json();
      setSettings({
        ...EMPTY_SETTINGS,
        ...(data.settings || {}),
        charge_campaign_ids: (data.settings?.charge_campaign_ids || []).map(String),
      });
      enqueueSnackbar("Настройки видимости сохранены", {
        variant: "success",
        autoHideDuration: 2000,
        anchorOrigin: { vertical: "top", horizontal: "right" },
      });
      handleClose();
    } catch (e) {
      enqueueSnackbar(e.message || "Ошибка сохранения", {
        variant: "error",
        autoHideDuration: 2500,
        anchorOrigin: { vertical: "top", horizontal: "right" },
      });
    } finally {
      setSaving(false);
    }
  };

  const updatedAtLabel = settings.updated_at
    ? new Date(settings.updated_at).toLocaleString("ru-RU")
    : null;

  const clientLabel =
    client?.email ||
    [client?.first_name, client?.surname].filter(Boolean).join(" ") ||
    client?.id;

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={style} className="visibilityBlocksModal">
        <Typography variant="h6" component="h2" className="vbmTitle">
          Видимость блоков
        </Typography>
        <Typography className="vbmClient">Клиент: {clientLabel}</Typography>

        {loading ? (
          <div className="vbmLoading">
            <CircularProgress size={28} />
          </div>
        ) : (
          <>
            <div className="vbmSection">
              <div className="vbmRow">
                <div>
                  <div className="vbmRowTitle">Скрыть настройки РК</div>
                  <div className="vbmRowHint">Убирает иконку настроек кампании</div>
                </div>
                <Switch
                  checked={Boolean(settings.hide_campaign_settings)}
                  onChange={(e) => setFlag("hide_campaign_settings", e.target.checked)}
                  color="primary"
                />
              </div>

              <div className="vbmRow">
                <div>
                  <div className="vbmRowTitle">Скрыть скачивание PostView</div>
                  <div className="vbmRowHint">Убирает кнопку «Скачать PostView»</div>
                </div>
                <Switch
                  checked={Boolean(settings.hide_postview_download)}
                  onChange={(e) => setFlag("hide_postview_download", e.target.checked)}
                  color="primary"
                />
              </div>

              <div className="vbmRow">
                <div>
                  <div className="vbmRowTitle">Скрыть фильтр объявлений</div>
                </div>
                <Switch
                  checked={Boolean(settings.hide_ads_filter)}
                  onChange={(e) => setFlag("hide_ads_filter", e.target.checked)}
                  color="primary"
                />
              </div>

              <div className="vbmRow">
                <div>
                  <div className="vbmRowTitle">Скрыть фильтр целей</div>
                  <div className="vbmRowHint">Скрывает фильтр по лидам / целям</div>
                </div>
                <Switch
                  checked={Boolean(settings.hide_goals_filter)}
                  onChange={(e) => setFlag("hide_goals_filter", e.target.checked)}
                  color="primary"
                />
              </div>
            </div>

            <div className="vbmSection">
              <div className="vbmSectionTitle">Метрики статистики</div>
              <div className="vbmRowHint" style={{ marginBottom: 8 }}>
                Скрывает выбранные карточки метрик во всём личном кабинете клиента
              </div>

              <div className="vbmRow">
                <div>
                  <div className="vbmRowTitle">Скрыть CTR (%)</div>
                </div>
                <Switch
                  checked={Boolean(settings.hide_stat_ctr)}
                  onChange={(e) => setFlag("hide_stat_ctr", e.target.checked)}
                  color="primary"
                />
              </div>

              <div className="vbmRow">
                <div>
                  <div className="vbmRowTitle">Скрыть eCPM (₽)</div>
                </div>
                <Switch
                  checked={Boolean(settings.hide_stat_ecpm)}
                  onChange={(e) => setFlag("hide_stat_ecpm", e.target.checked)}
                  color="primary"
                />
              </div>

              <div className="vbmRow">
                <div>
                  <div className="vbmRowTitle">Скрыть CR (%)</div>
                </div>
                <Switch
                  checked={Boolean(settings.hide_stat_cr)}
                  onChange={(e) => setFlag("hide_stat_cr", e.target.checked)}
                  color="primary"
                />
              </div>

              <div className="vbmRow">
                <div>
                  <div className="vbmRowTitle">Скрыть CPA</div>
                </div>
                <Switch
                  checked={Boolean(settings.hide_stat_cpa)}
                  onChange={(e) => setFlag("hide_stat_cpa", e.target.checked)}
                  color="primary"
                />
              </div>
            </div>

            <div className="vbmSection">
              <div className="vbmSectionTitle">Списания CPC / CPM</div>
              <div className="vbmRowHint" style={{ marginBottom: 12 }}>
                Включить списание только для выбранных кампаний с оптимизацией CPC/CPM
              </div>
              {campaigns.length === 0 ? (
                <div className="vbmEmpty">Нет кампаний с CPC/CPM</div>
              ) : (
                <div className="vbmCampaignList">
                  {campaigns.map((c) => {
                    const id = String(c.id);
                    const enabled = (settings.charge_campaign_ids || [])
                      .map(String)
                      .includes(id);
                    return (
                      <div className="vbmCampaignRow" key={id}>
                        <div className="vbmCampaignInfo">
                          <span className="vbmCampaignName">{c.name}</span>
                          <span className={`vbmBadge vbmBadge_${c.charge_type}`}>
                            {c.charge_type}
                          </span>
                        </div>
                        <Switch
                          checked={enabled}
                          onChange={(e) => toggleCampaign(id, e.target.checked)}
                          color="primary"
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="vbmSection">
              <div className="vbmSectionTitle">Дата начала статистики</div>
              <div className="vbmRowHint" style={{ marginBottom: 10 }}>
                Клиент не сможет выбрать период раньше этой даты
              </div>
              <div className="vbmDateRow">
                <TextField
                  type="date"
                  size="small"
                  value={settings.stats_visible_from || ""}
                  onChange={(e) =>
                    setFlag("stats_visible_from", e.target.value || null)
                  }
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 200 }}
                />
                {settings.stats_visible_from && (
                  <button
                    type="button"
                    className="vbmResetDate"
                    onClick={() => setFlag("stats_visible_from", null)}
                  >
                    Сбросить
                  </button>
                )}
              </div>
            </div>

            <div className="vbmAudit">
              {settings.updated_by_email && updatedAtLabel
                ? `Последнее изменение: ${settings.updated_by_email} · ${updatedAtLabel}`
                : "Ещё не изменялось"}
            </div>

            <div className="buttonGroup">
              <div className="resetButton" onClick={handleClose}>
                Отменить
              </div>
              <div
                className={`confirmButton${saving ? " disabled" : ""}`}
                onClick={() => {
                  if (!saving) handleSave();
                }}
              >
                {saving ? "Сохранение…" : "Сохранить"}
              </div>
            </div>
          </>
        )}
      </Box>
    </Modal>
  );
}

export default VisibilityBlocksModal;
